import React from "react";
import { Control, actions } from "react-redux-form";
import { Row, Col } from "reactstrap";
import { compose, withHandlers, withPropsOnChange } from "recompose";
import shortid from "shortid";
import { get, pick } from "lodash";
import { Trans } from "react-i18next";
import { NotificationManager } from "react-notifications";
import {
  FormGroup,
  DatePicker,
  required,
  Page,
  onMount,
  withReload,
  withLocalForm,
} from "components/common";
import {
  withXeroErrors,
  LineItems,
  SalesEditActions,
  withContactSearch,
  Attachments,
  withOptions,
  withDocumentRenderer,
} from "components";
import {
  BillCreditNoteDocument,
  InvoiceCreditNoteDocument,
} from "components/documents";
import { withOrganisationContextConsumer } from "../../../contexts/OrganisationContext";
import BackButton from "components/refactoring/BackButton";

export const EditRepeating = ({
  item,
  LocalForm,
  onSubmit,
  xero,
  handleSave,
  dispatch,
  handlePaymentAdded,
  handleAttachmentsChanged,
  ContactSearch,
  CreditNoteOptions,
  XeroErrors,
  t,
  payable,
  DocumentRenderer,
  EmailDocument,
  DownloadDocument,
  i18n,
  organisation,
}) => {

  const accPayCheck = /purchases|payable|accounts_payable/
  const currentUrl = window.location.href;
  const isPayable = accPayCheck.test(currentUrl);

  const typeR = isPayable ? 'ACCPAY' : 'ACCREC';

  const unitOptions = [
    { value: 'MONTHLY', label: t`Month(s)` },
    { value: 'WEEKLY', label: t`Week(s)` },
  ];

  const dueDateTypeOptions = [
    { value: 'OFFOLLOWINGMONTH', label: t`of the following month` },
    { value: 'DAYSAFTERBILLDATE', label: t`day(s) after bill date` },
    { value: 'DAYSAFTERINVOICEMONTH', label: t`day(s) after invoice month` },
    { value: 'OFCURRENTMONTH', label: t`of the current month` },
  ];

    const unitModel = item?.Schedule?.Unit || undefined

    const filteredDueDateTypeOptions = unitModel === 'WEEKLY'
    ? dueDateTypeOptions.filter(option => option.value === 'DAYSAFTERBILLDATE' || option.value === 'OFFOLLOWINGMONTH')
    : dueDateTypeOptions;

    const viewLink = () => {
        const { RepeatingInvoiceID } = item;
      
        window.location.href = `/accounts_receivable/repeating/view/${RepeatingInvoiceID}`;
      };

    const cStyle = {
      common: {
        component: "select",
        style: { width: '80%', border: '1px solid #e1e1e1', borderRadius: '4px', padding: '1px'  }
      },
      column: {
        className: "pt-1 pb-1",
        xs: false,
        md: 6,
      },
    };

    return (

  <div>
    <Page
      title={
        item.RepeatingInvoiceID
          ? t(`Duplicate Repeating Invoice {{ RepeatingInvoiceID }}`, {
              RepeatingInvoiceID: item?.Reference,
            })
          : t("Create New Repeating Invoice")
      }
      translate={false}
    >
      <BackButton/>
      <DocumentRenderer
        component={payable ? BillCreditNoteDocument : InvoiceCreditNoteDocument}
      />
      <XeroErrors />
      <div className="d-flex">
        {item.SentToContact && (
          <div className="text-success small">
            <span className="fa fa-check mr-1" /> <Trans>Sent to contact</Trans>
          </div>
        )}
        <div className="mx-auto" />
        {!!item.RepeatingInvoiceID && <DownloadDocument className="mr-2" />}
        {!!(item.RepeatingInvoiceID && !payable) && (
          <EmailDocument type="CREDIT_NOTE" item={item} />
        )}
        {!!(item.RepeatingInvoiceID && item.Attachments) && (
          <Attachments
            xero={xero}
            hideIncludeOnline
            onAttachmentsChanged={handleAttachmentsChanged}
            baseUrl={`repeatinginvoices/${item.RepeatingInvoiceID}`}
            attachments={item.Attachments}
          />
        )}
        {!!item.RepeatingInvoiceID && <div className="">
          <button
              type="button"
              className="btn-sm btn btn-primary"
              onClick={viewLink}>
              View Invoice
            </button>
          </div>}
      </div>
      <LocalForm
        onSubmit={onSubmit}
        validators={{
          Contact: { required },
          "LineItems[].Description": { required },
        }}
      >
        <Row>
        <Col {...cStyle.column}>
            <FormGroup
              label={payable ? "From" : "To"}
              model=".Contact"
              addControlClass={false}  
            >
              <ContactSearch model=".Contact" style={{backgroundColor:'#F4F7FE'}}/>
            </FormGroup>
          </Col>
          <Col {...cStyle.column}>
            <FormGroup label="Status">
              <Control.text model=".Status" readOnly />
            </FormGroup>
          </Col>
          <Col {...cStyle.column}>
            <FormGroup label="Reference" model=".Reference">
              <Control.text updateOn="blur" model=".Reference" />
            </FormGroup>
          </Col>
          <Col style={{ display: 'none' }}>
            <FormGroup label="Type" model=".Type">
              <Control.text updateOn="blur" model=".Type" defaultValue={typeR}/>
            </FormGroup>
          </Col>
          <Col {...cStyle.column}>
            <FormGroup label="Invoice Start Date" model=".Schedule.StartDate" >
                <DatePicker model=".Schedule.StartDate" className="form-control" />
            </FormGroup>
          </Col>
          <Col xs={12} md={6} className="pt-1 pb-1">
            <FormGroup label="Repeats Every" model="Schedule">
              <div style={{ display: 'flex', gap: '10px' }}>
                <Control.text model=".Schedule.Period" style={{ width: '20%' }} type="number"/>
                <Control.select {...cStyle.common}
                  model=".Schedule.Unit"
                  >
                  {unitOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                      {option.label}
                      </option>
                  ))}
                </Control.select>
              </div>
            </FormGroup>
          </Col>
          <Col xs={12} md={6} className="pt-1 pb-1">
            <FormGroup label="Due Date">
              <div style={{ display: 'flex', gap: '10px' }}>
                <Control.text model=".Schedule.DueDate" style={{ width: '20%' }} type="number"/>
                <Control.select {...cStyle.common}
                  model=".Schedule.DueDateType"
                  >
                  {filteredDueDateTypeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                      {option.label}
                      </option>
                  ))}
                </Control.select>
              </div>
            </FormGroup>
          </Col>
          <Col {...cStyle.column}>
            <FormGroup label="End Date" >
            <DatePicker model=".Schedule.EndDate" className="form-control" />
            </FormGroup>
          </Col>
        </Row>
        <Row> 
        </Row>
        <hr />
        <LineItems
          item={item}
          dispatch={dispatch}
          xero={xero}
        />
        <SalesEditActions onSave={handleSave} item={item} />
      </LocalForm>
    </Page>
  </div>
)};

const enhance = compose(
  withOrganisationContextConsumer,
  withPropsOnChange(["payable"], ({ payable }) => {
    const type = payable ? "ACCPAY" : "ACCREC";
    return {
      type,
      payable: !!payable,
    };
  }),
  withReload(),
  onMount(({ match, xero, location, type, fetchOrganisation }) => () => {
    const { id } = match.params;

    let item = null;

    if (id === "new") {
      item = get(location, "state.copy") || {
        LineItems: [],
        Status: "DRAFT",
        Type: type,
      };
    } else {
      item = xero
        .get(`repeatinginvoices/${match.params.id}`)
        .get("data")
        .then((x) => ({
          ...x,
          LineItems: x.LineItems.map((i) => ({
            ...i,
            _id: shortid(),
          })),
        }));
    }

    return Promise.props({
      item,
      organisation: fetchOrganisation(),
    });
  }),
  withLocalForm({
    initialState: ({ item }) => item,
    stateKey: "item",
    formKey: "form",
  }),
  withContactSearch(),
  withXeroErrors(),
  withHandlers({
    handleAttachmentsChanged:
      ({ xero, match, dispatch }) =>
      () => {
        xero
          .get(`repeatinginvoices/${match.params.id}/attachments`)
          .get("data")
          .tap((d) => dispatch(actions.change("local.Attachments", d)));
      },
    handleSave:
      ({ item, xero, reload, history, match, setXeroErrors, t, form }) =>
      ({ draft, approve, addAnother } = {}) => {
        console.log("firm", form)
        if (!form.$form.valid) return;
        let Status = item.Status;
        if (draft) Status = "DRAFT";
        if (approve) Status = "AUTHORISED";
        setXeroErrors(null);
        const { RepeatingInvoiceID } = item;
        const isNew = !RepeatingInvoiceID;

        const allowedProperties = "Type,Contact,Status,LineAmountTypes,LineItems,CurrencyCode,Reference".split(",");

        const scheduleProperties = Object.keys(item.Schedule || {}).filter(
            property => property !== 'NextScheduledDate' && property !== 'NextScheduledDateString'
          );
        
        const allowedSchedule = pick(item.Schedule, scheduleProperties);

        const allowedItem = {
            ...pick(item, allowedProperties),
            Schedule: allowedSchedule,
          };

          const p = isNew
          ? xero.post("repeatinginvoices", {
              ...allowedItem,
              RepeatingInvoiceID: undefined,
              Status,
            })
          : xero.put(`repeatinginvoices/${RepeatingInvoiceID}`, {
              ...allowedItem,
              Status,
            });

        p.then(({ data: { RepeatingInvoiceID } }) => {
          NotificationManager.success(t("Repeating Invoice saved successfully"));

          if (addAnother) {
            console.log("Redirecting to new repeating invoice");
            history.push(match.path.replace(":id", "new"));
          } else {
            console.log("Redirecting to view repeating invoice");
            history.replace(`/accounts_receivable/repeating/view/${RepeatingInvoiceID}`);
          }
          reload();
        }, setXeroErrors);
      },
    handlePaymentAdded:
      ({ reload }) =>
      () => {
        reload();
      },
  }),
  withOptions(),
  withDocumentRenderer({
    onDownload:
      ({ item }) =>
      (getDocument) =>
        getDocument(
          {
            item,
          },
          `creditnote_${item.RepeatingInvoiceID}.pdf`
        ),
  })
);

export default enhance(EditRepeating);
